import { useEffect, useMemo, useState } from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { convertDoubleToCurrencyPTBR } from "../../../../../utils/ConverterUtils";
import { formatDate } from "../../../../../utils/DateUtils";
import { ApiError } from "../../../Common/ApiError";
import { successAlert, warnAlert } from "../../../alert/defaultAlert";
import ModalPageEvent from "../../../Common/Modal/ModalPageEvent";
import LoadingCenter from "../../../Common/LoadingCenter";
import EmployeePaymentFields, { useEmployeePaymentFields } from "./EmployeePaymentFields";
import "./EmployeePaymentModal.css";

/** Situação de cada linha, devolvida pelo servidor (EmployeePaymentResource.BatchItemStatus). */
const READY = 'READY';
const REASON_KEYS = {
    ALREADY_PAID: 'cash_flow.employee_payment_batch_already_paid',
    NOTHING_TO_PAY: 'cash_flow.employee_payment_batch_nothing_to_pay'
};

/** Chave de uma linha: a mesma pessoa em duas filiais são duas linhas e dois pagamentos. */
const keyOf = (item) => `${item.employee?.id}:${item.branch?.id}`;

/**
 * "Pagar todos": registra de uma vez o pagamento dos colaboradores da tela Comissões, com a
 * mesma data, forma e categoria.
 *
 * A lista e os valores vêm do servidor (preview-all), pelo mesmo cálculo do pagamento
 * individual. Quem já foi pago no período ou não tem valor a receber aparece desmarcado e
 * travado, com o motivo, para o gestor ver a equipe inteira e não só quem vai receber.
 *
 * O envio é tudo ou nada: se algum colaborador for recusado, nenhum pagamento é gravado e a
 * mensagem diz quem barrou.
 *
 * @param {boolean} show - Controla a abertura do modal.
 * @param {function} setShow - Fecha o modal.
 * @param {object} period - { startDate, endDate } no formato YYYY-MM-DD, o filtro da tela.
 * @param {number} scopeBranchId - Filial do cabeçalho, nula em "Todas as filiais".
 * @param {boolean} isSimulating - A tela está com base ou percentual do pote alterados.
 * @param {function} onSaved - Chamado depois de registrar, para a tela recarregar.
 */
const EmployeePaymentBatchModal = ({ show, setShow, period, scopeBranchId, isSimulating, onSaved }) => {

    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fields = useEmployeePaymentFields();
    const { reset } = fields;

    // Cada abertura busca a lista de novo e marca todos que podem ser pagos: entre uma abertura e
    // outra alguém pode ter sido pago pelo botão individual.
    useEffect(() => {
        if (!show) {
            return;
        }

        setItems([]);
        setSelected(new Set());
        reset();
        setLoading(true);

        axiosInstance.get(`${Api.cash_flow.employee_payment}/preview-all`, {
            params: { startDate: period.startDate, endDate: period.endDate, scopeBranchId }
        })
            .then(({ data }) => {
                const rows = data || [];
                setItems(rows);
                setSelected(new Set(rows.filter(item => item.status === READY).map(keyOf)));
            })
            .catch((error) => {
                setShow(false);
                ApiError(error);
            })
            .finally(() => setLoading(false));
    }, [show, period, scopeBranchId, setShow, reset]);

    const payable = useMemo(() => items.filter(item => item.status === READY), [items]);
    const chosen = useMemo(() => payable.filter(item => selected.has(keyOf(item))), [payable, selected]);
    const total = chosen.reduce((sum, item) => sum + Number(item.netValue || 0), 0);
    // A filial só aparece na linha quando a lista tem mais de uma; numa filial só ela é ruído.
    const showBranch = new Set(items.map(item => item.branch?.id)).size > 1;
    const allChosen = payable.length > 0 && chosen.length === payable.length;

    /** Marca ou desmarca uma linha que pode ser paga. */
    const toggle = (item) => {
        setSelected(current => {
            const next = new Set(current);
            const key = keyOf(item);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    /** Marca todos que podem ser pagos, ou desmarca todos quando já estão marcados. */
    const toggleAll = () => {
        setSelected(allChosen ? new Set() : new Set(payable.map(keyOf)));
    };

    /** Envia só colaborador e filial de cada linha; os valores o servidor recalcula. */
    const confirm = () => {
        if (chosen.length === 0) {
            warnAlert(t('cash_flow.employee_payment_batch_none_selected'));
            return;
        }
        if (!fields.validate()) {
            return;
        }

        setIsSubmitting(true);
        axiosInstance.post(`${Api.cash_flow.employee_payment}/batch`, {
            periodStart: period.startDate,
            periodEnd: period.endDate,
            scopeBranchId,
            ...fields.toPayload(),
            items: chosen.map(item => ({ employee: { id: item.employee.id }, branch: { id: item.branch.id } }))
        })
            .then(({ data }) => {
                fields.remember();
                setShow(false);
                successAlert(t('cash_flow.employee_payment_batch_success', { count: (data || []).length }), () => onSaved?.());
            })
            .catch((error) => ApiError(error))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <ModalPageEvent
            show={show}
            setShow={setShow}
            centered={true}
            size="lg"
            title={t('cash_flow.employee_payment_batch_title')}
            btnText={chosen.length > 0
                ? t('cash_flow.employee_payment_batch_confirm', { count: chosen.length })
                : t('confirm')}
            btnOnClick={confirm}
            btnDisabled={isSubmitting || loading || chosen.length === 0}
            child={loading ? <LoadingCenter /> : (
                <Row className="g-3">
                    <Col xs={12}>
                        <div className="employee-payment__header">
                            <strong>{t('cash_flow.employee_payment_period_range', { start: formatDate(period.startDate), end: formatDate(period.endDate) })}</strong>
                            <span>{t('cash_flow.employee_payment_batch_hint')}</span>
                        </div>
                    </Col>

                    {items.length === 0 ? (
                        <Col xs={12}>
                            <p className="text-muted mb-0">{t('cash_flow.employee_payment_batch_empty')}</p>
                        </Col>
                    ) : (
                        <Col xs={12}>
                            <div className="employee-payment-batch">
                                <label className="employee-payment-batch__row is-head">
                                    <Form.Check
                                        type="checkbox"
                                        checked={allChosen}
                                        disabled={payable.length === 0}
                                        onChange={toggleAll}
                                        aria-label={t('cash_flow.employee_payment_batch_select_all')}
                                    />
                                    <span className="employee-payment-batch__name">{t('cash_flow.employee_payment_batch_select_all')}</span>
                                    <span className="employee-payment-batch__value">{t('cash_flow.employee_payment_net')}</span>
                                </label>
                                <div className="employee-payment-batch__list">
                                    {items.map(item => {
                                        const ready = item.status === READY;
                                        return (
                                            <label
                                                key={keyOf(item)}
                                                className={`employee-payment-batch__row${ready ? '' : ' is-disabled'}`}
                                            >
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={ready && selected.has(keyOf(item))}
                                                    disabled={!ready}
                                                    onChange={() => toggle(item)}
                                                    aria-label={item.employee?.name}
                                                />
                                                <span className="employee-payment-batch__name">
                                                    <strong>{item.employee?.name}</strong>
                                                    {showBranch && <small>{item.branch?.name}</small>}
                                                    {!ready && (
                                                        <span className="employee-payment-batch__reason">{t(REASON_KEYS[item.status])}</span>
                                                    )}
                                                </span>
                                                <span className={`employee-payment-batch__value${Number(item.netValue) < 0 ? ' is-negative' : ''}`}>
                                                    {convertDoubleToCurrencyPTBR(item.netValue)}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                                <div className="employee-payment-batch__total">
                                    <span>{t('cash_flow.employee_payment_batch_selected', { count: chosen.length })}</span>
                                    <strong>{convertDoubleToCurrencyPTBR(total)}</strong>
                                </div>
                            </div>
                        </Col>
                    )}

                    {isSimulating && (
                        <Col xs={12}>
                            <Alert variant="info" className="employee-payment__alert">
                                <i className="fa-solid fa-circle-info me-2"></i>
                                {t('cash_flow.employee_payment_simulation_hint')}
                            </Alert>
                        </Col>
                    )}

                    {payable.length > 0 && <EmployeePaymentFields fields={fields} />}
                </Row>
            )}
        />
    );
};

export default EmployeePaymentBatchModal;
